/* ==========================================================================
   shopService — Estate Marketplace (Lagos). Real (Supabase) mode only: in the
   demo/mock mode the Shop tab simply stays hidden.

   Reads go straight to tables (row-level security lets a resident see only
   their own orders). Every change goes through a database function that
   re-checks who is asking, so nothing here is the security boundary.
   ========================================================================== */

import { config } from "../config";
import { getSupabase } from "./supabaseClient";

export interface ShopCategory { id: string; slug: string; name: string; icon: string; image_url: string | null; tagline: string | null; sort: number }
export interface ShopProduct { id: string; category_id: string; slug: string; name: string; icon: string; image_url: string | null; unit: string; options: string[]; sort: number }
export interface ShopOrder {
  id: string; order_no: string; status: string; customer_name: string; customer_phone: string; estate_name: string;
  delivery_address: string; delivery_notes: string | null; preferred_date: string | null; preferred_window: string | null;
  substitution: string; budget_kobo: number | null; market: string | null; price_date: string | null;
  subtotal_kobo: number | null; logistics_kobo: number; service_fee_kobo: number; other_fee_kobo: number; other_fee_label: string | null;
  total_kobo: number | null; invoice_no: string | null; invoice_note: string | null; invoice_issued_at: string | null;
  invoice_valid_until: string | null; paid_at: string | null; delivered_at: string | null; cancelled_reason: string | null;
  created_at: string; updated_at: string; shop_order_items?: { count: number }[];
}
export interface ShopItem {
  id: string; product_name: string; icon: string | null; image_url: string | null; option_label: string | null; unit: string | null;
  qty: number; note: string | null; available: boolean; unit_price_kobo: number | null; line_total_kobo: number | null; owner_note: string | null;
}
export interface ShopEvent { id: number; status: string; note: string | null; created_at: string }
export interface ShopReview { id: string; rating: number; rating_quality: number | null; rating_delivery: number | null; rating_value: number | null; recommend: number | null; comment: string | null; owner_reply: string | null; created_at: string }
export interface LastPrice {
  product_id: string | null; product_name: string; option_label: string | null; unit: string | null; last_price_kobo: number; last_date: string;
  previous_price_kobo: number | null; previous_date: string | null; change_pct: number | null; times_bought: number; market: string | null;
}
export interface TrendPoint { price_date: string; unit_price_kobo: number; market: string | null }
export interface ShopMessage { id: number; sender_side: "resident" | "owner"; body: string; created_at: string }
export interface PlaceOrderItem { product_id: string; option_label: string | null; qty: number; note: string }
export interface DeliveryDetails { phone: string; address_extra: string; notes: string; preferred_date: string; window: string; substitution: string; budget_naira: string }

function sb() { return getSupabase(); }
function fail(e: { message: string } | null): never { throw new Error(e?.message ?? "Something went wrong"); }

async function available(): Promise<boolean> {
  if (config.useMockServices) return false;
  try {
    const { data, error } = await sb().rpc("shop_available");
    if (error) return false;
    return data === true;
  } catch { return false; }
}

async function catalog(): Promise<{ categories: ShopCategory[]; products: ShopProduct[] }> {
  const [c, p] = await Promise.all([
    sb().from("shop_categories").select("*").order("sort"),
    sb().from("shop_products").select("*").eq("active", true).order("sort"),
  ]);
  if (c.error) fail(c.error);
  if (p.error) fail(p.error);
  return { categories: (c.data ?? []) as ShopCategory[], products: (p.data ?? []) as ShopProduct[] };
}

async function placeOrder(items: PlaceOrderItem[], d: DeliveryDetails): Promise<ShopOrder> {
  const { data, error } = await sb().rpc("shop_place_order", { p_items: items, p_delivery: d });
  if (error) fail(error);
  return data as ShopOrder;
}

async function listOrders(): Promise<ShopOrder[]> {
  const { data, error } = await sb().from("shop_orders").select("*, shop_order_items(count)").order("created_at", { ascending: false }).limit(100);
  if (error) fail(error);
  return (data ?? []) as ShopOrder[];
}

async function getOrder(id: string): Promise<{ order: ShopOrder; items: ShopItem[]; events: ShopEvent[]; review: ShopReview | null }> {
  const [o, i, e, r] = await Promise.all([
    sb().from("shop_orders").select("*").eq("id", id).single(),
    sb().from("shop_order_items").select("*").eq("order_id", id).order("sort"),
    sb().from("shop_order_events").select("*").eq("order_id", id).order("created_at"),
    sb().from("shop_reviews").select("*").eq("order_id", id).maybeSingle(),
  ]);
  if (o.error) fail(o.error);
  return { order: o.data as ShopOrder, items: (i.data ?? []) as ShopItem[], events: (e.data ?? []) as ShopEvent[], review: (r.data ?? null) as ShopReview | null };
}

async function cancelOrder(id: string, reason: string): Promise<void> {
  const { error } = await sb().rpc("shop_cancel_order", { p_order_id: id, p_reason: reason });
  if (error) fail(error);
}

async function payOrder(id: string): Promise<void> {
  const ref = "SHOP-" + Date.now().toString(36).toUpperCase() + Math.random().toString(36).slice(2, 6).toUpperCase();
  const { error } = await sb().rpc("shop_pay_order", { p_order_id: id, p_reference: ref });
  if (error) fail(error);
}

async function submitReview(r: { orderId: string; rating: number; quality: number; delivery: number; value: number; recommend: number | null; comment: string }): Promise<void> {
  const { error } = await sb().rpc("shop_submit_review", {
    p_order_id: r.orderId, p_rating: r.rating, p_quality: r.quality, p_delivery: r.delivery, p_value: r.value,
    p_recommend: r.recommend, p_comment: r.comment,
  });
  if (error) fail(error);
}

async function lastPrices(): Promise<LastPrice[]> {
  const { data, error } = await sb().rpc("shop_last_prices");
  if (error) fail(error);
  return (data ?? []) as LastPrice[];
}

async function priceTrend(name: string, option: string | null): Promise<TrendPoint[]> {
  const { data, error } = await sb().rpc("shop_price_trend", { p_product_name: name, p_option_label: option });
  if (error) fail(error);
  return (data ?? []) as TrendPoint[];
}

async function profileContact(userId: string): Promise<{ phone: string }> {
  try {
    const { data } = await sb().from("profiles").select("phone").eq("id", userId).maybeSingle();
    return { phone: (data?.phone as string | null) ?? "" };
  } catch { return { phone: "" }; }
}

async function listMessages(orderId: string): Promise<ShopMessage[]> {
  const { data, error } = await sb().from("shop_messages").select("id, sender_side, body, created_at").eq("order_id", orderId).order("created_at");
  if (error) fail(error);
  return (data ?? []) as ShopMessage[];
}
async function sendMessage(orderId: string, body: string): Promise<void> {
  const { error } = await sb().rpc("shop_send_message", { p_order_id: orderId, p_body: body });
  if (error) fail(error);
}
async function markRead(orderId: string): Promise<void> {
  await sb().rpc("shop_mark_read", { p_order_id: orderId });
}
async function unreadCounts(): Promise<Record<string, number>> {
  try {
    const { data } = await sb().rpc("shop_unread_counts");
    const out: Record<string, number> = {};
    for (const r of (data ?? []) as { order_id: string; unread: number }[]) out[r.order_id] = r.unread;
    return out;
  } catch { return {}; }
}

export const shopService = { listMessages, sendMessage, markRead, unreadCounts, available, catalog, placeOrder, listOrders, getOrder, cancelOrder, payOrder, submitReview, lastPrices, priceTrend, profileContact };
